import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BsCart2, BsArrowLeft, BsPlus, BsDash } from 'react-icons/bs';
import swal from 'sweetalert';
import { getProductById } from '../api/products';
import useOrder from '../hooks/useOrder';
import LoadingScreen from '../components/LoadingScreen/LoadingScreen';

const highlightImportant = (text) => {
  return text.replace(/\b([A-ZÀ-Ü0-9]{2,})\b/g, '<span class="font-bold">$1</span>');
};

const ProductDetailScreen = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { handleCart } = useOrder();
  const [id, setId] = useState(null);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    const storedId = localStorage.getItem('selected_product_id');
    const storedPromo = localStorage.getItem('selected_product_promo');
    if (!storedId) {
      navigate('/products');
    } else {
      setId(storedId);
      setPromo(storedPromo);
    }
  }, [navigate]);

  useEffect(() => {
    if (!id) return;
    const fetchProduct = async () => {
      try {
        const res = await getProductById(id, promo);
        setProduct(res.data.product);
      } catch (err) {
        console.error('Erreur de chargement du produit', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, promo]);

  const addToCart = async () => {
    try {
      const cartItem = { ...product, quantity };
      await handleCart(cartItem);
      swal('✅ Ajouté', 'Produit ajouté au panier', 'success');
    } catch (err) {
      console.error('Erreur ajout panier:', err);
      swal('❌ Erreur', 'Impossible d’ajouter au panier', 'error');
    }
  };

  if (loading) return <LoadingScreen />;
  if (!product) return null;

  const now = new Date();
  const promoIsActive =
    product.promoPercentage &&
    product.promoStartDate &&
    new Date(product.promoStartDate) <= now &&
    (!product.promoEndDate || new Date(product.promoEndDate) >= now);

  const promoPrice = promoIsActive
    ? (product.initialPrice * (1 - product.promoPercentage / 100)).toFixed(2)
    : null;

  const unitPrice = promoPrice ? parseFloat(promoPrice) : product.initialPrice;
  const totalPrice = (unitPrice * quantity).toFixed(2);

  return (
    <section className="max-w-screen-xl mx-auto px-4 md:px-6 py-20">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
        <div className="relative bg-gray-100 rounded-xl p-4 flex items-center justify-center">
          {promoIsActive && (
            <div className="absolute top-4 left-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow z-10">
              -{product.promoPercentage}%
            </div>
          )}
          <img
            src={product.image}
            alt={product.name}
            className="max-h-[450px] w-full object-contain rounded"
          />
        </div>

        <div className="space-y-6">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{product.name}</h1>
          <p
            className="text-gray-600 text-base md:text-lg whitespace-pre-line"
            dangerouslySetInnerHTML={{ __html: highlightImportant(product.description) }}
          />
          <div className="text-sm text-gray-500">
            {product.category && (
              <p>
                Catégorie : <span className="font-medium">{product.category.name}</span>
              </p>
            )}
            {product.subcategory && (
              <p>
                Sous-catégorie : <span className="font-medium">{product.subcategory.name}</span>
              </p>
            )}
          </div>

          <div className="text-xl md:text-2xl font-semibold text-paragaia">
            {promoPrice ? (
              <>
                <span className="line-through text-gray-400 mr-2">{product.initialPrice} TND</span>
                <span>{promoPrice} TND</span>
              </>
            ) : (
              <span>{product.initialPrice} TND</span>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <button
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-md"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            >
              <BsDash />
            </button>
            <span className="text-lg font-medium">{quantity}</span>
            <button
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-md"
              onClick={() => setQuantity((q) => q + 1)}
            >
              <BsPlus />
            </button>
          </div>

          <div className="text-lg font-semibold">
            Total : <span className="text-paragaia">{totalPrice} TND</span>
          </div>

          <p className="text-sm text-gray-600 italic">Livraison +7 TND</p>

          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <button
              className="flex items-center justify-center gap-2 px-6 py-3 bg-paragaia text-white hover:bg-paragaiaLight text-sm rounded-md"
              onClick={addToCart}
            >
              <BsCart2 />
              Passer commande
            </button>
            <button
              className="flex items-center justify-center gap-2 px-6 py-3 border border-blue-500 text-blue-500 hover:bg-blue-50 text-sm rounded-md"
              onClick={() => {
                localStorage.removeItem('selected_product_id');
                localStorage.removeItem('selected_product_promo');
                navigate('/products');
              }}
            >
              <BsArrowLeft />
              Retour aux produits
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDetailScreen;
